import React, {useContext, useEffect, useRef, useState} from 'react';
import {getAccountRatingCounts, getReviewFeed} from '../../../util/requestSender';
import {AppContext} from '../../misc/appContext';
import {ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import ReviewFeed from './reviewFeed';
import ReviewFeedMobile from './reviewFeedMobile';
import {useBreakpoint} from '../../../util/useBreakpoint';
import {createErrorMessage, setsAreEqual} from '../../../util/utils';
import {userHasRight} from '../../../util/accessUtil';
import {AccessKey} from '../../../util/interfaces';
import {Rating} from '../../../util/types';
import ProductReview from '../../../models/productReviewModel';
import {LAST_REVIEW_FLEX_KEY} from '../../../util/constants';
import {DefaultNumberObject} from '../../../util/defaultObject';
import {ReviewCountsRef} from '../reviewCounts/reviewCounts';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const LIMIT = 20;

function getInitialFilter(): Set<Rating> {
    const filter = new URLSearchParams(location.search).getAll('filter');
    const initialFilter: Set<Rating> = new Set();
    for (const value of filter) {
        const numFilter = Number(value);
        if (isNaN(numFilter)) continue;
        if (numFilter <= 5 && numFilter >= 1) initialFilter.add(numFilter as Rating);
    }
    return initialFilter;
}

type LoadParams = {scrollToTop?: boolean; saveTotal?: boolean; ratingsFilter?: Set<Rating>};

export default function ReviewFeedController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const bp = useBreakpoint();
    const isMobileLayout = bp.isMobileLayout;
    const [reviews, setReviews] = useState<Array<ProductReview>>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [ratingCounts, setRatingCounts] = useState(new DefaultNumberObject<Rating>(undefined, () => -1));
    const [ratingsFilter, setRatingsFilter] = useState<Set<Rating>>(getInitialFilter());
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterTotal, setFilterTotal] = useState(0);
    const page = useRef(0);
    // Monotonic id of the latest full (page 0) feed request — responses of
    // older requests (a superseded filter change, or a load-more racing a
    // reload) are dropped instead of clobbering the newer list.
    const requestSeq = useRef(0);
    // Id of the last reload whose response was applied. While it trails
    // requestSeq a reload is in flight and load-mores must wait — a page-1
    // fetched next to a pending page-0 could land first and mix result sets.
    const appliedSeq = useRef(0);
    // Cursor and filter of the list currently on screen. Error paths restore
    // these so the next load-more extends what the user actually sees, instead
    // of continuing from a cursor/filter whose pages never made it into the
    // list (holes or mixed result sets).
    const appliedPage = useRef(0);
    const appliedFilter = useRef<Set<Rating>>(ratingsFilter);
    const filterRef = useRef<ReviewCountsRef>(null);

    function saveLastSeen() {
        const {account} = context.accountContext;
        if (!account) return;
        account.setFlex(LAST_REVIEW_FLEX_KEY, IDate.now().format());
        account.update();
    }

    function onOpenFilter() {
        setFilterOpen(true);
    }

    function onCloseFilter() {
        setFilterOpen(false);
        const selectedRatings = filterRef.current?.getCurrentSelected();
        if (selectedRatings === undefined) return;
        setRatingsFilter(selectedRatings);
        loadReviewFeed({scrollToTop: true, saveTotal: false, ratingsFilter: selectedRatings});
    }

    function onClearRatingFilter() {
        setRatingsFilter(new Set());
        loadReviewFeed({saveTotal: false, scrollToTop: true, ratingsFilter: new Set()});
    }

    // Mobile filter path: the generic PortalSelect reports the full selection on
    // every change — no ref read-back on close like the desktop popover.
    function onChangeRatingsFilter(ratings: Array<Rating>) {
        const nextFilter = new Set(ratings);
        setRatingsFilter(nextFilter);
        loadReviewFeed({saveTotal: false, scrollToTop: true, ratingsFilter: nextFilter});
    }

    function onReviewStatClick(evt: CustomEvent<'all' | 'positive' | 'negative'>) {
        const stat = evt.detail;
        let ratings: Set<Rating> = new Set();
        if (stat === 'positive') ratings = new Set([4, 5]);
        else if (stat === 'negative') ratings = new Set([1, 2, 3]);
        if (setsAreEqual(ratings, ratingsFilter)) return;
        setReviews([]);
        window.setTimeout(() => {
            setRatingsFilter(ratings);
            loadReviewFeed({saveTotal: false, scrollToTop: true, ratingsFilter: ratings});
        }, 1);
    }

    function onLoadMore(callback: VoidFunction) {
        if (!account) return;
        if (requestSeq.current !== appliedSeq.current) {
            // A reload's page 0 is still in flight — fetching page 1 now could
            // land first and append onto the outgoing list.
            window.setTimeout(callback);
            return;
        }
        page.current += 1;
        const requestedPage = page.current;
        setLoadingMore(true);
        const seq = requestSeq.current;
        getReviewFeed({
            data: {
                account_id: account.id,
                page: requestedPage,
                limit: LIMIT,
                filter_ratings: Array.from(ratingsFilter)
            },
            callback: (data: ResponseData) => {
                setLoadingMore(false);
                if (seq !== requestSeq.current) {
                    // A full reload (e.g. a filter change) superseded this page —
                    // appending it would mix two different result sets.
                    window.setTimeout(callback);
                    return;
                }
                if (data.success) {
                    const _reviews: Array<ProductReview> = data.reviews.map((rev: any) => new ProductReview(rev));
                    setReviews(previous => previous.concat(_reviews));
                    appliedPage.current = requestedPage;
                } else {
                    // The page never made it into the list — rewind the cursor,
                    // or the next load-more would skip it (a hole in the feed).
                    page.current = appliedPage.current;
                    if (!data.handled)
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'reviewAnalytics.reviewFeed.loadFeedFailed',
                                        'Het laden van de feed is niet gelukt.'
                                    )
                            ),
                            t('reviewAnalytics.reviewFeed.loadFailed', 'Laden mislukt')
                        );
                }
                window.setTimeout(callback);
            },
            errorCallback: () => {
                // Network failure / non-2xx: `callback` never fires (see the
                // Callback contract) — release the loading state and rewind the
                // cursor here. No dialog: the next scroll (desktop) or the
                // load-more button (mobile) simply retries.
                setLoadingMore(false);
                if (seq === requestSeq.current) page.current = appliedPage.current;
                window.setTimeout(callback);
            }
        });
    }

    function loadReviewFeed(params?: LoadParams) {
        if (!account) return;
        setLoading(true);
        // A full reload restarts the feed at page 0 — reset the pagination
        // cursor so the next load-more fetches page 1 of the NEW result set
        // instead of continuing where the previous filter left off.
        page.current = 0;
        requestSeq.current += 1;
        const seq = requestSeq.current;
        const _ratingsFilter = params?.ratingsFilter ?? ratingsFilter;
        const saveTotal = params?.saveTotal ?? true;
        const scrollToTop = params?.scrollToTop ?? false;
        // The reload failed and the previously applied list is still shown —
        // line the cursor AND the filter back up with it, so the next
        // load-more extends the visible result set instead of appending pages
        // of a different filter (mixed sets) or skipping pages (holes).
        function revertToApplied() {
            page.current = appliedPage.current;
            setRatingsFilter(appliedFilter.current);
        }
        getReviewFeed({
            data: {account_id: account.id, page: 0, limit: LIMIT, filter_ratings: Array.from(_ratingsFilter)},
            callback: (data: ResponseData) => {
                if (seq !== requestSeq.current) return; // Superseded by a newer reload
                appliedSeq.current = seq;
                setLoading(false);
                if (data.success) {
                    const _reviews: Array<ProductReview> = data.reviews.map((rev: any) => new ProductReview(rev));
                    setReviews(_reviews);
                    appliedPage.current = 0;
                    appliedFilter.current = _ratingsFilter;
                    if (saveTotal) setFilterTotal(data.total);
                    setTotal(data.total);
                    if (scrollToTop)
                        // Deferred past React's commit: scrolling the moment
                        // setReviews lands would animate against the OLD list —
                        // the DOM swap mid-animation makes Chrome abort the
                        // smooth scroll partway up.
                        window.setTimeout(() => {
                            // `div.page` is the actual scroll container (window
                            // scrolling is a no-op inside the app layout).
                            const pageElement = document.querySelector('div.page');
                            if (pageElement) pageElement.scrollTo({top: 0, behavior: 'smooth'});
                            else window.scrollTo({top: 0, behavior: 'smooth'});
                        });
                } else {
                    revertToApplied();
                    if (!data.handled)
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'reviewAnalytics.reviewFeed.loadFeedFailed',
                                        'Het laden van de feed is niet gelukt.'
                                    )
                            ),
                            t('reviewAnalytics.reviewFeed.loadFailed', 'Laden mislukt')
                        );
                }
            },
            errorCallback: () => {
                // Network failure / non-2xx: `callback` never fires (see the
                // Callback contract). Resolve this generation here — otherwise
                // `loading` hangs forever and the appliedSeq guard would block
                // every future load-more.
                if (seq !== requestSeq.current) return;
                appliedSeq.current = seq;
                setLoading(false);
                revertToApplied();
                showDialog(
                    createErrorMessage(
                        t('reviewAnalytics.reviewFeed.loadFeedFailed', 'Het laden van de feed is niet gelukt.')
                    ),
                    t('reviewAnalytics.reviewFeed.loadFailed', 'Laden mislukt')
                );
            }
        });
    }

    function loadRatingCounts() {
        if (!account) return;
        getAccountRatingCounts({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (data.success) {
                    const {rating_counts} = data;
                    setRatingCounts(
                        new DefaultNumberObject<Rating>(
                            {
                                1: rating_counts.rating_one,
                                2: rating_counts.rating_two,
                                3: rating_counts.rating_three,
                                4: rating_counts.rating_four,
                                5: rating_counts.rating_five
                            },
                            () => 0
                        )
                    );
                }
            }
        });
    }

    useEffect(() => {
        if (!userHasRight(context.accountContext.account, AccessKey.RATING_VIEW)) return;
        loadReviewFeed();
        saveLastSeen();
        loadRatingCounts();
    }, [account?.id]);

    useEffect(() => {
        if (filterOpen)
            window.setTimeout(() => {
                document.body.addEventListener('click', onCloseFilter);
            });
        else document.body.removeEventListener('click', onCloseFilter);
        return function () {
            document.body.removeEventListener('click', onCloseFilter);
        };
    }, [filterOpen, onCloseFilter]);

    useEffect(() => {
        document.addEventListener('reviewStatClick', onReviewStatClick);

        return function () {
            document.removeEventListener('reviewStatClick', onReviewStatClick);
        };
    }, [ratingsFilter]);

    // The bottom-nav breakpoint range gets the simplified card feed; the
    // desktop keeps the classic feed with the ReviewCounts filter popover.
    if (isMobileLayout)
        return (
            <ReviewFeedMobile
                onChangeRatingsFilter={onChangeRatingsFilter}
                onLoadMore={onLoadMore}
                reviews={reviews}
                loading={loading}
                loadingMore={loadingMore}
                total={total}
                ratingCounts={ratingCounts}
                ratingsFilter={ratingsFilter}
            />
        );
    return (
        <ReviewFeed
            onLoadMore={onLoadMore}
            onOpenFilter={onOpenFilter}
            onCloseFilter={onCloseFilter}
            onClearRatingFilter={onClearRatingFilter}
            reviews={reviews}
            loading={loading}
            loadingMore={loadingMore}
            total={total}
            ratingCounts={ratingCounts}
            ratingsFilter={ratingsFilter}
            filterOpen={filterOpen}
            filterTotal={filterTotal}
            filterRef={filterRef}
        />
    );
}
